import {
  DataFetcher,
  DataFetcherRequest,
  PriorityConfig,
} from '@aboutyou/data-fetcher';
import {ProductImageTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {StreamTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  GetShopStreamResponse,
  GetStreamResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  GetProductStreamResponse,
  GetProductStreamV2Response,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/service.types';
import {ErrorPageInline, Slot} from '@aboutyou/ui-components';
import React, {FC, memo} from 'react';
import {ProductStreamSkeleton} from './ProductStreamSkeleton';
import {ProductStreamStructureMarkup} from './ProductStreamStructureMarkup';
import {StreamTileList, StreamTileListProps} from './StreamTileList';
import {PRODUCTS_PER_PAGE} from './constants';

export interface TilesViewOffsetLimitProps
  extends StreamTileListProps,
    PriorityConfig {
  page: number;
  preferredProductImageType?: ProductImageTypes;
  request: (page: number) => DataFetcherRequest<any>;
  hideTopUSP?: boolean;
  enableImageGalleryAnimation?: boolean;
  errorComponent?: React.FC;
}

const Tiles: FC<TilesViewOffsetLimitProps> = ({
  request,
  renderingPriority,
  errorComponent = ErrorComponent,
  ...viewProps
}) => {
  return (
    <DataFetcher
      request={request(viewProps.page)}
      Loading={LoadingComponent}
      Error={errorComponent}
      viewProps={viewProps}
      // Only the first page should be prioritized against other components on the first viewport.
      // On further pages we expect that other components are already loaded.
      // If every page is prioritized, this causes a re-rendering of the whole page which can harm performance.
      renderingPriority={viewProps.page === 1 ? renderingPriority : undefined}
      View={View}
    />
  );
};

const View = ({
  preferredProductImageType,
  ...props
}: (
  | GetShopStreamResponse
  | GetProductStreamResponse
  | GetProductStreamV2Response
  | GetStreamResponse
) &
  TilesViewOffsetLimitProps) => {
  let itemsPreferences: ProductTilePreferences | undefined;
  if ('itemsPreferences' in props) {
    itemsPreferences = props.itemsPreferences;
  }
  const itemsPreferencesMerged = preferredProductImageType
    ? ({
        ...itemsPreferences,
        preferredProductImageType,
      } as ProductTilePreferences)
    : itemsPreferences;

  let trackingData = props.trackingData;
  if ('tracker' in props) {
    trackingData = {...(props.tracker as StreamTracker)};
  }

  return (
    <>
      {props.page === 1 && <ProductStreamStructureMarkup items={props.items} />}
      <StreamTileList
        {...props}
        itemsPreferences={itemsPreferencesMerged!}
        trackingData={trackingData}
      />
    </>
  );
};

const LoadingComponent: FC = () => {
  // Loading 32 product tile makes sure we render close to the same amount of tiles that the backend will potentially return.
  // As a result, layout shifts if the user scrolls fast are reduced and then if the load more button is pressed we don't double fetch.
  // With the former approach of loading only 6 product tiles we always fetch 2 batches at once
  // as the loading component was already close to the viewport when the former component was still in loading state.
  return <ProductStreamSkeleton amount={PRODUCTS_PER_PAGE} />;
};

const ErrorComponent: FC = () => {
  return (
    <Slot position={1} columns={3}>
      <ErrorPageInline />
    </Slot>
  );
};

/**
 * @deprecated Please use `TilesByPage` instead, especially for new streams
 */
export const TilesByPageOffsetLimit = memo(Tiles);
